import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  DollarSign,
  ArrowLeft,
  Check,
  X,
  HelpCircle,
  Lock,
  Share2,
  MessageCircle,
  Send,
  MoreHorizontal,
  Edit,
  Trash2,
  UserPlus
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { EventInviteDialog } from './events/EventInviteDialog';
import { EventShareDialog } from './events/EventShareDialog';
import { eventDateValue, eventIsPast } from './events/eventSharing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = {
  casual: { label: 'Casual Round', icon: '🏌️' },
  tournament: { label: 'Tournament', icon: '🏆' },
  outing: { label: 'Golf Outing', icon: '👥' },
  lesson: { label: 'Lesson/Clinic', icon: '📚' },
  other: { label: 'Other', icon: '⛳' },
};

// Participant List Item
const ParticipantItem = ({ participant, isOrganizer }) => {
  const getStatusBadge = (status) => {
    switch (status) {
      case 'going': return { color: 'bg-green-500', text: 'Going' };
      case 'maybe': return { color: 'bg-yellow-500', text: 'Maybe' };
      case 'not_going': return { color: 'bg-red-500', text: 'Not Going' };
      case 'invited': return { color: 'bg-blue-500', text: 'Invited' };
      default: return { color: 'bg-gray-500', text: status };
    }
  };

  const badge = getStatusBadge(participant.status);

  return (
    <div className="flex items-center justify-between gap-2 py-2" data-testid={`event-participant-${participant.user_id}`}>
      <div className="flex items-center gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={participant.picture} />
          <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
            {participant.name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium text-[var(--ps-text-primary)]">
            {participant.name}
            {participant.is_organizer && (
              <span className="text-xs text-[var(--ps-primary)] ml-2">Organizer</span>
            )}
          </p>
          {participant.handicap !== null && (
            <p className="text-xs text-[var(--ps-text-muted)]">{participant.handicap} HCP</p>
          )}
        </div>
      </div>
      <span data-testid={`event-participant-status-${participant.user_id}`} className={`text-xs shrink-0 px-2 py-1 rounded-full text-white ${badge.color}`}>
        {badge.text}
      </span>
    </div>
  );
};

// Comment Item
const CommentItem = ({ comment }) => {
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  return (
    <div className="flex gap-3 py-3">
      <Avatar className="w-8 h-8 flex-shrink-0">
        <AvatarImage src={comment.user_picture} />
        <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] text-xs">
          {comment.user_name?.charAt(0) || 'U'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="bg-[var(--ps-bg-hover)] rounded-xl p-3">
          <p className="font-medium text-sm text-[var(--ps-text-primary)]">{comment.user_name}</p>
          <p className="text-sm text-[var(--ps-text-secondary)] mt-1">{comment.content}</p>
        </div>
        <p className="text-xs text-[var(--ps-text-muted)] mt-1 ml-3">{formatTime(comment.created_at)}</p>
      </div>
    </div>
  );
};

// Main Event Detail Page
export const EventDetailPage = ({ user }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // details, participants, comments
  const [showInvite, setShowInvite] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [loadError, setLoadError] = useState('');

  const isOrganizer = event?.organizer_id === user?.user_id;
  const eventDate = event ? eventDateValue(event.event_date) : null;
  const isPast = eventIsPast(event);

  // Fetch event
  const fetchEvent = useCallback(async () => {
    setLoadError(''); setLoading(true);
    try {
      const response = await axios.get(`${API}/social-events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      setEvent(null);
      setLoadError(error.response?.status === 403 ? 'This event is invite-only. Ask the organizer to invite your Par Seeker account.' : error.response?.status === 404 ? 'This event is no longer available.' : 'Could not load this event. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // RSVP
  const handleRSVP = async (status) => {
    try {
      const response = await axios.post(`${API}/social-events/${eventId}/rsvp`, { status });
      setEvent(response.data);
      toast.success(status === 'going' ? "You're going!" : "RSVP updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to RSVP");
    }
  };

  // Add comment
  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/social-events/${eventId}/comment?content=${encodeURIComponent(commentText)}`);
      setCommentText('');
      fetchEvent(); // Refresh to get new comment
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  // Delete event
  const handleDeleteEvent = async () => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    
    try {
      await axios.delete(`${API}/social-events/${eventId}`);
      toast.success("Event deleted");
      navigate('/events');
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="dark-spinner" />
      </div>
    );
  }

  if (!event) return <div className="p-6 space-y-4" data-testid="event-unavailable"><p role="alert" data-testid="event-load-error">{loadError}</p><Button onClick={fetchEvent} data-testid="event-load-retry">Retry</Button><Button variant="ghost" onClick={() => navigate('/events')} data-testid="event-unavailable-back">Back to Events</Button></div>;

  const typeInfo = eventTypes[event.event_type] || eventTypes.other;
  const goingParticipants = event.participants?.filter(p => p.status === 'going') || [];
  const maybeParticipants = event.participants?.filter(p => p.status === 'maybe') || [];
  const invitedParticipants = event.participants?.filter(p => p.status === 'invited') || [];
  const declinedParticipants = event.participants?.filter(p => p.status === 'not_going') || [];

  return (
    <div className="pb-4" data-testid="event-detail-page">
      {/* Header with Back Button */}
      <div className="sticky top-0 z-10 bg-[var(--ps-bg-base)] border-b border-[var(--ps-border)]">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => navigate('/events')}
            data-testid="event-back"
            className="flex items-center gap-2 text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Events</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShare(true)} data-testid="event-share-open" aria-label="Share event" title="Share event"
              className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)]"
            >
              <Share2 className="w-5 h-5" />
            </button>
            {isOrganizer && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="event-actions-menu" aria-label="Event actions" className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)]">
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]" data-testid="event-actions-dropdown">
                  <DropdownMenuItem onClick={() => setShowInvite(true)} data-testid="event-actions-invite" className="text-[var(--ps-text-primary)]">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite members
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-red-500"
                    onClick={handleDeleteEvent}
                    data-testid="event-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Event
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Cover Image */}
      {event.cover_image && (
        <div className="relative h-48 bg-[var(--ps-bg-surface)]">
          <img 
            src={event.cover_image} 
            alt={event.title} 
            className="w-full h-full object-cover"
          />
          {event.is_private && (
            <div className="absolute top-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Private Event
            </div>
          )}
        </div>
      )}

      {/* Event Info */}
      <div className="p-4">
        {/* Type Badge & Title */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-[var(--ps-primary-light)] flex items-center justify-center text-3xl flex-shrink-0">
            {typeInfo.icon}
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs text-[var(--ps-primary)] font-medium uppercase tracking-wider">
              {typeInfo.label}
            </span>
            <h1 className="text-2xl font-bold text-[var(--ps-text-primary)] mt-1 break-words" data-testid="event-title">{event.title}</h1>
            <p className="text-sm text-[var(--ps-text-muted)] mt-1">
              Organized by {event.organizer_name}
            </p>
          </div>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="dark-card p-3">
            <div className="flex items-center gap-2 text-[var(--ps-primary)] mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs font-medium">Date</span>
            </div>
            <p className="font-semibold text-[var(--ps-text-primary)]">
              {eventDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </p>
          </div>
          <div className="dark-card p-3">
            <div className="flex items-center gap-2 text-[var(--ps-primary)] mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Time</span>
            </div>
            <p className="font-semibold text-[var(--ps-text-primary)]">
              {event.start_time || 'TBD'}
            </p>
          </div>
          <div className="dark-card p-3">
            <div className="flex items-center gap-2 text-[var(--ps-primary)] mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Going</span>
            </div>
            <p className="font-semibold text-[var(--ps-text-primary)]">
              {goingParticipants.length}
              {event.max_participants && <span className="text-[var(--ps-text-muted)]"> / {event.max_participants}</span>}
            </p>
          </div>
          <div className="dark-card p-3">
            <div className="flex items-center gap-2 text-[var(--ps-primary)] mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs font-medium">Entry Fee</span>
            </div>
            <p className="font-semibold text-[var(--ps-text-primary)]">
              {event.entry_fee > 0 ? `$${event.entry_fee}` : 'Free'}
            </p>
          </div>
        </div>

        {/* Location */}
        {(event.course_name || event.location) && (
          <div className="dark-card p-4 mb-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[var(--ps-primary)]" />
              <div>
                <p className="font-medium text-[var(--ps-text-primary)]">
                  {event.course_name || event.location}
                </p>
                {event.course_name && event.location && (
                  <p className="text-sm text-[var(--ps-text-muted)]">{event.location}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* RSVP Buttons */}
        {isOrganizer && <div className="flex flex-wrap gap-2 mb-4"><Button variant="outline" onClick={() => setShowInvite(true)} data-testid="event-invite-open"><UserPlus size={16} className="mr-2" />Invite members</Button><Button variant="outline" onClick={() => setShowShare(true)} data-testid="event-share-action"><Share2 size={16} className="mr-2" />Share event</Button></div>}
        {isOrganizer && event.notification_delivery?.pending_count > 0 && <p role="status" className="text-sm mb-4" data-testid="event-notifications-pending">Some invitation notifications are pending. <button className="underline text-[var(--ps-primary)]" onClick={() => setShowShare(true)} data-testid="event-notifications-recover">Review delivery</button></p>}
        {event.is_private && <p className="text-sm text-[var(--ps-text-muted)] mb-4" data-testid="event-private-status">Private · Invite only</p>}
        {event.user_rsvp === 'invited' && <p className="text-sm text-[var(--ps-primary)] mb-4" data-testid="event-invited-status">You're invited</p>}
        {!isPast && !isOrganizer && (
          <div className="flex gap-2 mb-6">
            <Button
              className={`flex-1 ${event.user_rsvp === 'going' ? 'dark-btn-primary' : 'dark-btn-secondary'}`}
              onClick={() => handleRSVP('going')}
              data-testid="event-rsvp-going"
            >
              <Check className="w-4 h-4 mr-2" />
              Going
            </Button>
            <Button
              variant="ghost"
              className={`flex-1 ${event.user_rsvp === 'maybe' ? 'text-yellow-500 bg-yellow-500/10' : 'text-[var(--ps-text-secondary)]'}`}
              onClick={() => handleRSVP('maybe')}
              data-testid="event-rsvp-maybe"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Maybe
            </Button>
            <Button
              variant="ghost"
              className={`${event.user_rsvp === 'not_going' ? 'text-red-500 bg-red-500/10' : 'text-[var(--ps-text-secondary)]'}`}
              onClick={() => handleRSVP('not_going')}
              data-testid="event-rsvp-decline" aria-label="Not going"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 border-b border-[var(--ps-border)] mb-4">
          {['details', 'participants', 'comments'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`event-tab-${tab}`}
              className={`pb-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab 
                  ? 'text-[var(--ps-primary)] border-b-2 border-[var(--ps-primary)]'
                  : 'text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]'
              }`}
            >
              {tab}
              {tab === 'participants' && ` (${event.participants?.length || 0})`}
              {tab === 'comments' && ` (${event.comments?.length || 0})`}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div>
            {event.description ? (
              <p className="text-[var(--ps-text-secondary)] whitespace-pre-wrap">{event.description}</p>
            ) : (
              <p className="text-[var(--ps-text-muted)] italic">No description provided.</p>
            )}
          </div>
        )}

        {activeTab === 'participants' && (
          <div>
            {[[invitedParticipants, 'Invited'], [declinedParticipants, 'Not going']].map(([participants, label]) => participants.length > 0 && <section className="mb-4" key={label} data-testid={`event-participants-${label === 'Invited' ? 'invited' : 'declined'}`}><h3 className="text-sm font-medium text-[var(--ps-text-muted)] mb-2">{label} ({participants.length})</h3><div className="divide-y divide-[var(--ps-border)]">{participants.map(p => <ParticipantItem key={p.user_id} participant={p} />)}</div></section>)}
            {goingParticipants.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-[var(--ps-text-muted)] mb-2">
                  Going ({goingParticipants.length})
                </h3>
                <div className="divide-y divide-[var(--ps-border)]">
                  {goingParticipants.map((p) => (
                    <ParticipantItem key={p.user_id} participant={p} />
                  ))}
                </div>
              </div>
            )}
            {maybeParticipants.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-[var(--ps-text-muted)] mb-2">
                  Maybe ({maybeParticipants.length})
                </h3>
                <div className="divide-y divide-[var(--ps-border)]">
                  {maybeParticipants.map((p) => (
                    <ParticipantItem key={p.user_id} participant={p} />
                  ))}
                </div>
              </div>
            )}
            {event.participants?.length === 0 && (
              <p className="text-[var(--ps-text-muted)] text-center py-8">
                No participants yet. Be the first to RSVP!
              </p>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div>
            {/* Add Comment */}
            <div className="flex gap-3 mb-4">
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] text-xs">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 flex gap-2">
                <Input
                  value={commentText}
                  data-testid="event-comment-input"
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="dark-input flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <Button
                  onClick={handleAddComment}
                  data-testid="event-comment-send" aria-label="Send comment"
                  disabled={!commentText.trim() || submittingComment}
                  className="dark-btn-primary px-3"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Comments List */}
            {event.comments?.length > 0 ? (
              <div className="divide-y divide-[var(--ps-border)]">
                {event.comments.map((comment) => (
                  <CommentItem key={comment.comment_id} comment={comment} />
                ))}
              </div>
            ) : (
              <p className="text-[var(--ps-text-muted)] text-center py-8">
                No comments yet. Start the conversation!
              </p>
            )}
          </div>
        )}
      </div>
      {showInvite && <EventInviteDialog event={event} onClose={() => setShowInvite(false)} onInvited={setEvent} />}
      {showShare && <EventShareDialog event={event} isOrganizer={isOrganizer} onClose={() => setShowShare(false)} onDelivery={delivery => setEvent(previous => ({ ...previous, notification_delivery: delivery }))} />}
    </div>
  );
};

export default EventDetailPage;
